import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { NextScheduledActionsStyle } from "./NextScheduledActions.style";
import { useLoginContext } from "../../context";
import axios from "axios";
import ScheduledSongCard from "./ScheduledSongCard";
import ScheduledPlaylistCard from "./ScheduledPlaylistCard";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import { LoadingSpinner } from "../../components";

interface NextScheduledActionsProps {
  userPlaylists?: any[];
}

type TabType = "songs" | "playlists";
type TimeframeType = "today" | "week" | "month";

const NextScheduledActions: React.FC<NextScheduledActionsProps> = ({ userPlaylists = [] }) => {
  const navigate = useNavigate();
  const { state: loginState } = useLoginContext();
  const [activeTab, setActiveTab] = useState<TabType>("songs");
  const [timeframe, setTimeframe] = useState<TimeframeType>("today");
  const [scheduledSongs, setScheduledSongs] = useState<any>({ adding: [], removing: [] });
  const [scheduledPlaylists, setScheduledPlaylists] = useState<any>({ refreshes: [], resets: [] });
  const [loading, setLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  // Reset fetch flag when tab or timeframe changes
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [activeTab]);

  useEffect(() => {
    hasFetchedRef.current = false;
  }, [timeframe]);

  useEffect(() => {
    // Only fetch when we have stable login state and haven't fetched yet
    if (!loginState.token || !loginState.userId) {
      return;
    }

    if (hasFetchedRef.current) {
      return;
    }

    hasFetchedRef.current = true;

    if (activeTab === "songs") {
      fetchScheduledSongs();
    } else {
      fetchScheduledPlaylists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, timeframe, loginState.token]);

  const fetchScheduledSongs = async () => {
    if (!loginState.token || !loginState.userId) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/scheduled-songs`,
        {
          params: {
            userId: loginState.userId,
            timeframe: timeframe,
            access_token: loginState.token
          },
          headers: { Authorization: `Bearer ${loginState.token}` }
        }
      );
      // Add null safety for response data
      const data = response?.data?.data;
      setScheduledSongs({
        adding: Array.isArray(data?.adding) ? data.adding : [],
        removing: Array.isArray(data?.removing) ? data.removing : []
      });
    } catch (error) {
      console.error("Error fetching scheduled songs:", error);
      // Set empty arrays on error
      setScheduledSongs({ adding: [], removing: [] });
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledPlaylists = async () => {
    if (!loginState.userId || !loginState.token) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/scheduled-playlists`,
        {
          params: {
            userId: loginState.userId,
            timeframe: timeframe,
            access_token: loginState.token
          },
          headers: { Authorization: `Bearer ${loginState.token}` }
        }
      );

      // Enrich playlist data with userPlaylists if playlistData is null
      const enrichedData = {
        refreshes: Array.isArray(response.data.data.refreshes) && response.data.data.refreshes.map((item: any) => {
          if (!item.playlistData && userPlaylists.length > 0) {
            const playlist = userPlaylists.find((p: any) => p.id === item.playlist_id);
            if (playlist) {
              return {
                ...item,
                playlistData: {
                  name: playlist.name,
                  images: playlist.images,
                  tracks: playlist.tracks
                }
              };
            }
          }
          return item;
        }),
        resets: Array.isArray(response.data.data.resets) && response.data.data.resets.map((item: any) => {
          if (!item.playlistData && userPlaylists.length > 0) {
            const playlist = userPlaylists.find((p: any) => p.id === item.playlist_id);
            if (playlist) {
              return {
                ...item,
                playlistData: {
                  name: playlist.name,
                  images: playlist.images,
                  tracks: playlist.tracks
                }
              };
            }
          }
          return item;
        })
      };

      setScheduledPlaylists(enrichedData);
    } catch (error) {
      console.error("Error fetching scheduled playlists:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSongCardClick = (song: any) => {
    navigate(`/playlist/${song.selected_playlist_id}`);
  };

  const handlePlaylistCardClick = (playlistId: string) => {
    navigate(`/playlist/${playlistId}`);
  };

  return (
    <NextScheduledActionsStyle>
      <div className="section-header">
        <h2 className="section-title">Next scheduled actions</h2>
        <div className="timeframe-selector">
          <select value={timeframe} onChange={(e) => setTimeframe(e.target.value as TimeframeType)}>
            <option value="today">Today</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
          </select>
        </div>
      </div>

      <div className="tabs-container">
        <button
          className={`tab ${activeTab === "songs" ? "active" : ""}`}
          onClick={() => setActiveTab("songs")}
        >
          Songs
        </button>
        <button
          className={`tab ${activeTab === "playlists" ? "active" : ""}`}
          onClick={() => setActiveTab("playlists")}
        >
          Playlists
        </button>
      </div>

      {loading ? (
        <LoadingSpinner text="Loading scheduled actions..." containerPadding="40px 20px" />
      ) : (
        <div className="content-container">
          {activeTab === "songs" ? (
            <SongsTabContent 
              scheduledSongs={scheduledSongs}
              onCardClick={handleSongCardClick}
            />
          ) : (
            <PlaylistsTabContent 
              scheduledPlaylists={scheduledPlaylists}
              onCardClick={handlePlaylistCardClick}
            />
          )}
        </div>
      )}
    </NextScheduledActionsStyle>
  );
};

// Songs Tab Content Component
const SongsTabContent: React.FC<{
  scheduledSongs: { adding: any[]; removing: any[] };
  onCardClick: (song: any) => void;
}> = ({ scheduledSongs, onCardClick }) => {
  const hasAddingSongs = scheduledSongs.adding.length > 0;
  const hasRemovingSongs = scheduledSongs.removing.length > 0;
  const hasAnySongs = hasAddingSongs || hasRemovingSongs;
  const showSideBySide = hasAddingSongs && hasRemovingSongs;
  
  if (!hasAnySongs) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🎵</div>
        <div className="empty-title">No scheduled songs</div>
        <div className="empty-subtitle">
          You don&apos;t have any songs scheduled for this timeframe
        </div>
      </div>
    );
  }
  
  return (
    <div className={`songs-content ${showSideBySide ? 'side-by-side' : ''}`}>
      {hasAddingSongs && (
        <div className="songs-section">
          <h3 className="subsection-title">
            <span className="badge-adding">+</span>
            <span>To be added ({scheduledSongs.adding.length})</span>
          </h3>
          <div className="cards-grid">
            {Array.isArray(scheduledSongs.adding) && scheduledSongs.adding.map((song, index) => (
              <ScheduledSongCard
                key={`adding-${song._id || index}`}
                song={song}
                actionType="adding"
                onClick={onCardClick}
              />
            ))}
          </div>
        </div>
      )}
      
      {hasRemovingSongs && (
        <div className="songs-section">
          <h3 className="subsection-title">
            <span className="badge-removing">−</span>
            <span>To be removed ({scheduledSongs.removing.length})</span>
          </h3>
          <div className="cards-grid">
            {Array.isArray(scheduledSongs.removing) && scheduledSongs.removing.map((song, index) => (
              <ScheduledSongCard
                key={`removing-${song._id || index}`}
                song={song}
                actionType="removing"
                onClick={onCardClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Playlists Tab Content Component
const PlaylistsTabContent: React.FC<{
  scheduledPlaylists: { refreshes: any[]; resets: any[] };
  onCardClick: (playlistId: string) => void;
}> = ({ scheduledPlaylists, onCardClick }) => {
  const hasRefreshes = scheduledPlaylists.refreshes.length > 0;
  const hasResets = scheduledPlaylists.resets.length > 0;
  const hasAnyPlaylists = hasRefreshes || hasResets;
  const showSideBySide = hasRefreshes && hasResets;
  
  if (!hasAnyPlaylists) {
    return (
      <div className="empty-state">
        <div className="empty-icon">📋</div>
        <div className="empty-title">No scheduled playlist operations</div>
        <div className="empty-subtitle">
          You don&apos;t have any playlists scheduled for refresh or reseting
        </div>
      </div>
    );
  }
  
  return (
    <div className={`playlists-content ${showSideBySide ? 'side-by-side' : ''}`}>
      {hasRefreshes && (
        <div className="playlists-section">
          <h3 className="subsection-title">
            <span className="badge-refresh"><RefreshIcon /></span>
            <span>To be refreshed ({scheduledPlaylists.refreshes.length})</span>
          </h3>
          <div className="cards-grid">
            {Array.isArray(scheduledPlaylists.refreshes) && scheduledPlaylists.refreshes.map((playlist, index) => (
              <ScheduledPlaylistCard
                key={`refresh-${playlist._id || index}`}
                playlist={playlist}
                scheduleType="refresh"
                onClick={onCardClick}
              />
            ))}
          </div>
        </div>
      )}
      
      {hasResets && (
        <div className="playlists-section">
          <h3 className="subsection-title">
            <span className="badge-reset"><SettingsIcon /></span>
            <span>To be reset ({scheduledPlaylists.resets.length})</span>
          </h3>
          <div className="cards-grid">
            {Array.isArray(scheduledPlaylists.resets) && scheduledPlaylists.resets.map((playlist, index) => (
              <ScheduledPlaylistCard
                key={`reset-${playlist._id || index}`}
                playlist={playlist}
                scheduleType="reset"
                onClick={onCardClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NextScheduledActions;

